import {
  SESSION_KEYS,
  EvsesCompatibility,
  SMART_CHARGING_WALLBOX_DATA,
} from "src/consts/session";
import { useEffect, useState } from "react";
import Text from "src/components/Displays/Text";
import { Trans, useTranslation } from "react-i18next";
import { SMART_CHARGING_ROUTES } from "src/consts/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { Box, List, Stack, Divider, ListItem } from "@mui/material";
import { transferDetailUpdate } from "src/api/smartChargingClients";
import EvseListItem from "src/components/SmartCharging/Components/EvseListItem";

export interface CompatibilityFormValues {
  manufacturer: string;
  manufacturersList: {
    defaultList: string[];
    filteredList: string[];
  };
  model: string;
  modelsList: {
    defaultList: string[];
    filteredList: string[];
  };
  evsesCompatibility: {
    modelName: string;
    manufacturer: string;
    compatibilityStatus: string;
  }[];
}

const Model = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get("theme");
  const { t } = useTranslation("translation");
  const manufacturer = searchParams.get("manufacturer") || "";
  const [getTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);

  const [getEvsesCompatibility] = useSessionStorage<EvsesCompatibility[]>(
    SESSION_KEYS.EVSES_COMPATIBILITY,
  );
  const [, setWallboxData] = useSessionStorage<SMART_CHARGING_WALLBOX_DATA>(
    SESSION_KEYS.SMART_CHARGING_WALLBOX_DATA,
  );

  const [evsesCompatibility, setEvsesCompatibility] = useState<
    EvsesCompatibility[]
  >([]);

  useEffect(() => {
    const evsesCompatibility = getEvsesCompatibility();
    if (evsesCompatibility) {
      const filteredManufacturers = evsesCompatibility.filter(
        (evse) =>
          evse.manufacturer.toLowerCase() === manufacturer.toLowerCase() &&
          evse.compatibilityStatus === "PREMIUM",
      );

      setEvsesCompatibility(filteredManufacturers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModelClick = async (evse: EvsesCompatibility) => {
    setWallboxData(evse);

    const transferId = getTransferId();
    if (!transferId) return;

    const payload = {
      manufacturer: evse.manufacturer,
      modelName: evse.modelName,
      compatibilityStatus: evse.compatibilityStatus,
    };

    try {
      await transferDetailUpdate({
        payload,
        transferId,
      });
      navigate(`${SMART_CHARGING_ROUTES.TERMS}`);
    } catch (e) {}
  };

  const handleSearchClick = () => {
    navigate(SMART_CHARGING_ROUTES.MANUFACTURER_SEARCH + `?theme=${theme}`);
  };

  return (
    <Stack gap={5}>
      <Stack gap={5}>
        <Text
          text={t("page.wallboxCompatibilityModel.pageTitle")}
          sx={(theme) => ({
            fontWeight: 500,
            fontSize: "32px",
            lineHeight: "40px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={t("page.wallboxCompatibilityModel.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack gap={5}>
        <Text
          text={t("page.wallboxCompatibilityModel.formTitle")}
          sx={(theme) => ({
            fontSize: "24px",
            fontWeight: "500",
            lineHeight: "28px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Box
          sx={(theme) => ({
            px: "16px",
            borderRadius: "12px",
            bgcolor: theme.palette.white.main,
          })}
        >
          <List>
            {evsesCompatibility &&
              evsesCompatibility.map((evse, index) => (
                <>
                  <EvseListItem
                    itemText={evse.modelName}
                    handleItemClick={() => handleModelClick(evse)}
                  />
                  {evsesCompatibility.length !== index + 1 && (
                    <Divider sx={{ ml: "16px", mr: "16px" }} />
                  )}
                </>
              ))}
            <ListItem disablePadding></ListItem>
          </List>
        </Box>
      </Stack>

      <Stack gap={2}>
        <Text
          text={t("page.wallboxCompatibilityModel.para1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "24px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={
            <Trans
              i18nKey={
                t(
                  "page.wallboxCompatibilityModel.para1Desc",
                ) as "page.wallboxCompatibilityModel.para1Desc"
              }
              t={t}
              components={{ bold: <strong /> }}
            />
          }
          sx={(theme) => ({
            fontSize: "12px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack>
        <PrimaryButton
          onClick={handleSearchClick}
          text={"Ich besitze eine andere Wallbox"}
          sx={{
            border: "1px solid #00000038",
            backgroundColor: "transparent",
            color: "#3B3B3B",
          }}
        />
      </Stack>
    </Stack>
  );
};

export default Model;
